import { BookkeepingTagNavItemValue } from '@components'

import { Contact } from '@features/contacts/types/contact'
import { Account } from '@modules-deprecated/app/accounts/types'
import { TaxRate } from '@modules-deprecated/app/taxrates/types'

import { BillFormLine, BillFormPartial } from './formData'
import { getDataFromBookkeepingTag } from './getDataFromBookkeepingTag'

interface GetBillFormFromBookkeepingTagProps {
  accounts?: Account[]
  bookkeepingTagValue: BookkeepingTagNavItemValue | undefined
  existingBillLines?: BillFormPartial['billLines']
  hasVATdetected: boolean | undefined
  lineIndex?: number
  linesTotal?: number
  organizationCountryCode: string
  taxRates?: TaxRate[]
  vendor?: Partial<Contact>
}

export const getBillFormFromBookkeepingTag = ({
  accounts,
  bookkeepingTagValue,
  existingBillLines,
  hasVATdetected,
  lineIndex = 0,
  linesTotal = 1,
  organizationCountryCode,
  taxRates,
  vendor,
}: GetBillFormFromBookkeepingTagProps): BillFormPartial => {
  const { account, taxRate } = bookkeepingTagValue
    ? getDataFromBookkeepingTag({
        accounts,
        bookkeepingTagValue,
        hasVATdetected,
        organizationCountryCode,
        taxRates,
        vendor,
      })
    : { account: undefined, taxRate: undefined }

  const billLines: Partial<BillFormLine>[] = []

  let onlyFirstLineFilled = true
  for (let i = 1; i < linesTotal; i++) {
    if (existingBillLines?.[i]?.expenseCategory || existingBillLines?.[i]?.bookkeepingTag) {
      onlyFirstLineFilled = false
      break
    }
  }

  for (let i = 0; i < linesTotal; i++) {
    const sourceIndex = onlyFirstLineFilled ? 0 : i
    const sourceLine = existingBillLines?.[sourceIndex] ?? {}
    const isEditedLine = i === lineIndex

    const updatedLine: Partial<BillFormLine> = {
      bookkeepingTag: sourceLine.bookkeepingTag,
      ...(isEditedLine
        ? {
            ...(account ? { expenseCategory: account } : {}),
            ...(taxRate && billLines[i]?.taxRate === undefined ? { taxRate } : {}),
          }
        : {
            expenseCategory: onlyFirstLineFilled ? account : sourceLine.expenseCategory,
          }),
    }

    billLines[i] = updatedLine
  }

  return {
    billLines,
  }
}

interface GetBillFormFromBookkeepingTagFormProps {
  accounts?: Account[]
  billForm: BillFormPartial
  organizationCountryCode: string
  taxRates?: TaxRate[]
  vendor?: Partial<Contact>
}

export const getBillFormFromBookkeepingTagForm = ({
  accounts,
  billForm,
  organizationCountryCode,
  taxRates,
  vendor,
}: GetBillFormFromBookkeepingTagFormProps) => {
  if (!billForm?.billLines?.length) {
    return {}
  }

  const billFormPartial: BillFormPartial = {
    billLines: [],
  }

  if (billFormPartial.billLines) {
    for (let i = 0; i < billForm.billLines.length; i++) {
      const bookkeepingTagValue = billForm.billLines[i].bookkeepingTag
      const { account, taxRate } = bookkeepingTagValue
        ? getDataFromBookkeepingTag({
            accounts,
            bookkeepingTagValue,
            hasVATdetected: billForm.billLines[i].hasVATdetected,
            organizationCountryCode,
            taxRates,
            vendor: vendor || billForm.vendor,
          })
        : {
            account: undefined,
            taxRate: undefined,
          }

      billFormPartial.billLines[i] = {
        ...(account ? { expenseCategory: account } : {}),
        ...(taxRate ? { taxRate } : {}),
      }
    }
  }

  return billFormPartial
}
